// src/oura/components/newscan/steps/StartStep.tsx
//
// Step 4, "Ready to scan": the approved 50 as facts, who asked
// and why, the paid note, then the go.
//
// The set is approved (frozen) on this step. Back reopens it and returns to
// Review. Save draft leaves the page; the draft is held on the server, and the
// two fields typed here are kept in this browser until the scan starts (the
// contract has no route that saves them before Start). Start the scan asks
// once in the foot's confirm row, because it spends money, then hands the
// notice to the screen it lands on: the business for an existing one, the
// businesses list for a new one.
//
// Brand mode (v4.5, SPEC-v45 §8) adds the markets, the Google searches the 50
// came from, the partner products, and what counts as a win.
//
// A signed client's First AI check (JOURNEY-SPEC v1.1 §2.5) says these 50
// become the baseline, adds From (the pitch, or drafted new) and For the plan
// (what Start writes to the plan), prefills Why this set with the quote, and
// shows the server's refusal inline (already running, already finished, brand
// checks not live, a provided set held). It returns to the onboarding page.
//
// One duration everywhere: FIRST_CHECK_MINUTES, never the server's estimate.

import { CheckIcon, GlobeIcon, PlayIcon } from "lucide-react";
import { useEffect, useId, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfileContext } from "@context/user_profile";
import type { ScanDraft } from "@service/aeo/scanDraftTypes";
import { getOnboarding } from "@service/aeo/onboarding";
import { cn } from "@oura/lib/cn";
import { OURA_ROOT } from "@oura/nav";
import { PillButton, Surface, TYPE } from "@oura/ui";
import { Refusal } from "@oura/components/onboarding/fields";
import { FIRST_CHECK_MINUTES } from "@oura/onboarding/derive";
import { inputClass, textareaClass } from "@oura/onboarding/messageText";
import { plural } from "@oura/newscan/copy";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { scanDraftStarted } from "@oura/newscan/scanDraftNotice";
import { StepHead } from "@oura/components/newscan/StepHead";
import { Muted } from "@oura/components/newscan/Muted";
import { DefRow } from "@oura/components/newscan/DefRow";
import { RadioSeg } from "@oura/components/newscan/RadioSeg";
import { shapeFromDraft } from "@oura/newscan/recipeShape";
import { useConnectedFocus } from "@oura/newscan/useConnectedFocus";
import { brandShape, isBrandSet, joinAnd, marketLine, marketsNotCaptured, shortDate } from "@oura/components/newscan/brandReview";
import { carriedText, forPlanText, fromText, isFirstCheck } from "@oura/components/newscan/firstCheckFacts";

const COPY = {
    title: "Ready to scan",
    checkingRun: "Checking the previous scan…",
    runCheckFailed: "We could not check the previous scan. Try again before starting another paid check.",
    retry: "Retry",
    frozen: (n: number, isNew: boolean) =>
        `These ${n} are frozen as ${isNew ? "set v1" : "the new set"}. Every rescan asks the same ones until someone opens a new draft.`,
    questions: "Questions",
    recipe: "Recipe",
    changes: "Your changes",
    noChanges: "None, the draft as written",
    gsc: "Search Console",
    gscConnected: "Connected",
    gscMatched: (n: number) => `${plural(n, "question")} ${n === 1 ? "matches" : "match"} real searches`,
    gscPulling: "reading the searches",
    gscNot: "Not connected.",
    gscConnect: "Connect",
    gscConnecting: "Connecting",
    gscWhy: "Optional. Adds search counts after the scan.",
    requestedBy: "Requested by",
    requestedByPlaceholder: "Who asked for this scan",
    why: "Why this set",
    whyHint: "Shows in Question history.",
    whyExample: {
        // New scan is for prospects only (JOURNEY-SPEC §0 line 1): the pitch, not a client's first scan.
        new_scan: "Pitch scan before the first meeting",
        edit_set: "What you changed and why",
        recipe_change: "Moved to the new recipe",
    },
    paidLead: "Paid scan.",
    paid: (n: number, minutes: number, cost: number) =>
        `ChatGPT app, Gemini and Google AI ask exactly these ${n}. About ${plural(minutes, "minute")}, about US$${cost.toFixed(2)}.`,
    back: "Back",
    save: "Save draft",
    start: "Start the scan",
    // The same confirm row on every kind (JOURNEY-SPEC §2.5).
    confirm: (cost: number) => `This starts a paid scan, about US$${cost.toFixed(2)}. Start it?`,
    confirmVerb: "Start",
    startedTitle: "Scan started",
    startedLine: "This set is with the engines now. The results land on the business page when the scan finishes.",
    startedClose: "Close",
    openBusiness: (name: string) => `Open ${name}`,
    /** A new business has no page yet: Close goes to the list, so the button says so. */
    toList: "Back to Businesses",
    markets: "Markets",
    searches: "Google searches",
    pulled: (date: string | null, n: number) => [date && `Pulled ${date}`, plural(n, "search", "searches")].filter(Boolean).join(" · "),
    partners: "Partner products",
    none: "None",
    win: "Counts as a win",
    winLine: (partners: number) =>
        partners > 0
            ? "A question counts when AI names you, cites your site, or names a partner product."
            : "A question counts when AI names you or cites your site.",
    // SPEC-v45 §12.1 default, still open with YW and Matthew: the ChatGPT app capture runs from
    // Malaysia, so rows for another market wait until each market has its own capture.
    notYet: (markets: string[]) =>
        `Questions for ${joinAnd(markets)} cannot be asked from their own market yet. Save the draft for now. Start opens when that is ready.`,
    footWaiting: (markets: string[]) => `Start waits for ${joinAnd(markets)}`,
} as const;

/**
 * A changed set on a business with a baseline (JOURNEY-SPEC §2.7, call 13
 * default): a new set and the baseline stays, or, in its first 30 days, a
 * setup fix that moves the baseline (call 12).
 */
const BASELINE = {
    label: "Baseline",
    // Short enough for half a phone's width: the line under the switch says the rest.
    keep: "Keep the baseline",
    move: "Move the baseline",
    keepLine: (day: string) => `The baseline stays on ${day}. Monthly reports compare only the questions both sets ask.`,
    moveLine: (day: string) => `This scan becomes the baseline in place of ${day}. Results before it stay in Question history.`,
    locked: (day: string, days: number) => `Stays on ${day}. A baseline moves only in its first ${days} days.`,
    reason: "Why the baseline moves",
    reasonPlaceholder: "What was set up wrong",
    reasonHint: "Shows in Question history.",
    reasonNeeded: "Say why the baseline moves.",
} as const;
const BASELINE_DAYS = 30;

/**
 * When a changed set applies, on a business whose monthly checks run (H2-c):
 * at the next monthly check (free, the default) or a rescan now (paid).
 */
const WHEN = {
    label: "When",
    later: "At the next monthly check",
    now: "Rescan now",
    laterLine: (day: string) => `Free. The monthly check on ${day} asks the new set. Nothing runs before then.`,
    nowLine: (cost: number) => `Paid. A rescan starts now, about US$${cost.toFixed(2)}.`,
    moveNeedsNow: "Moving the baseline needs a rescan now.",
    freeLead: "No scan now.",
    free: (day: string) => `The monthly check on ${day} asks these 50. Its report compares only the questions both sets ask.`,
    startLater: "Apply at the next check",
    title: "Ready to apply",
    frozen: (n: number) => `These ${n} are frozen as the new set. The next monthly check asks them, and every one after it.`,
    startedTitle: "The new set waits for the monthly check",
    startedLine: (day: string) => `On ${day}, the monthly check asks these 50. Nothing runs before then.`,
} as const;

/** A First AI check that takes over the pitch scan still running on the same 50 (H4-e). */
const PITCH_RUN = {
    lead: "No new scan.",
    line: (minutes: number) =>
        `The pitch scan running on these 50 becomes the first AI check. It finishes in about ${plural(minutes, "minute")}, then Oura readies the login and the access request.`,
} as const;

/** A signed client's First AI check (JOURNEY-SPEC §2.5). Final copy. */
const FIRST = {
    title: "Start the first AI check",
    sentence: (business: string) =>
        `These 50 become ${business}'s baseline. Every monthly check asks the same ones, and every report compares with this one.`,
    from: "From",
    forPlan: "For the plan",
    /** This draft's own edits against the pitch: the pitch's edits are already in "From". */
    changedHere: "Changed from the pitch",
    changedCount: (n: number, total: number) => `${n} of the ${total} questions`,
    nothingForPlan: "Nothing new",
    why: (ref: string | null) => (ref ? `First AI check. Signed ${ref}.` : "First AI check."),
    paidLead: "Paid check.",
    paid: (n: number, minutes: number, cost: number) =>
        `ChatGPT app, Gemini and Google AI ask exactly these ${n}. About ${plural(minutes, "minute")}, about US$${cost.toFixed(2)}. When it finishes, Oura readies the login and the access request.`,
    startedTitle: "The first AI check has started",
    startedLine: "These 50 are with the engines now. The onboarding page shows it running and readies the login when it finishes.",
} as const;

/** Refusals the server sends before spending on a First AI check: shown inline with its sentence. */
const START_REFUSALS = new Set(["scan_running", "scan_finished", "brand_not_live", "provided_set_held", "draft_open"]);

/* ------------------------------------------------------------------------- *
 * What the draft says, in words
 * ------------------------------------------------------------------------- */

/** Recipe update: the questions that keep the old recipe's wording. */
function keptRows(d: ScanDraft): number[] {
    const rc = d.recipeChange;
    if (d.kind !== "recipe_change" || !rc) return [];
    const rows = rc.rows.filter((r) => !r.same);
    const kept = rc.mode === "keep_all" ? rows : rc.mode === "choose_each" ? rows.filter((r) => !r.forced && r.pick === "keep") : [];
    return kept.map((r) => r.n);
}

/**
 * "Local v4.5", or for a recipe update "Local v4.5 · 3 kept from the old recipe". Shows the label
 * only: Local v4.5 is the v4.2 problem-led local questions unchanged, so its stamp stays
 * visibility-set-v4.2-problem-led, the stable identifier matching the backend's LOCAL_STAMP
 * (#4317, ourteam-backend#6066).
 */
function recipeText(d: ScanDraft): string {
    if (d.kind !== "recipe_change" || !d.recipeChange) return d.recipe.label;
    const kept = keptRows(d).length;
    return kept > 0 ? `${d.recipe.label} · ${kept} kept from the old recipe` : `${d.recipe.label} · every change taken`;
}

/**
 * "3 words changed · 1 reworded · 2 asked another way · 1 locked". Questions kept
 * on the old recipe are told on the Recipe line, not here. Null when every part is 0.
 */
function changesText(d: ScanDraft): string | null {
    const words = d.words.filter((w) => w.origin === "staff").length;
    const reworded = d.questions.filter((q) => q.reworded).length;
    const swapped = d.questions.filter((q) => q.swapped && !q.reworded).length;
    // A question kept on the old recipe holds its wording by design: it counts as kept, not locked.
    const kept = new Set(keptRows(d));
    const locked = d.questions.filter((q) => q.locked && !q.reworded && !kept.has(q.n)).length;
    const parts = [
        words > 0 && `${plural(words, "word")} changed`,
        reworded > 0 && `${reworded} reworded`,
        swapped > 0 && `${swapped} asked another way`,
        locked > 0 && `${locked} locked`,
    ];
    const text = parts.filter(Boolean).join(" · ");
    return text || null;
}

/* ------------------------------------------------------------------------- *
 * Who asked and why: kept in this browser until the scan starts
 * ------------------------------------------------------------------------- */

interface StartFields {
    /** null until typed in: the field shows the default (the draft's value, else the signed-in name). */
    requestedBy: string | null;
    /** null until typed in: the field shows the default (a First AI check's "First AI check. Signed …", else empty). */
    note: string | null;
}

const EMPTY_START_FIELDS: StartFields = { requestedBy: null, note: null };

/**
 * One key per signed-in staff member and draft: on a shared browser a second
 * staffer must not find, and submit under their name, the first one's unsent
 * requester and note (Jack, #4288).
 */
const fieldsKey = (owner: string, draftId: string) => `oura.newscan.start.v1.${encodeURIComponent(owner)}.${draftId}`;

function loadFields(key: string): StartFields | null {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const v = JSON.parse(raw) as Partial<StartFields> | null;
        if (!v) return null;
        return { requestedBy: typeof v.requestedBy === "string" ? v.requestedBy : null, note: typeof v.note === "string" ? v.note : null };
    } catch {
        return null;
    }
}

function saveFields(key: string, v: StartFields | null): void {
    try {
        if (v && (v.requestedBy !== null || v.note !== null)) window.localStorage.setItem(key, JSON.stringify(v));
        else window.localStorage.removeItem(key);
    } catch {
        // Storage off (private window): the fields still work, they are just not kept.
    }
}

/* ------------------------------------------------------------------------- *
 * The step
 * ------------------------------------------------------------------------- */

export default function StartStep() {
    const { ctl, kind, purpose, goTo, close, foot, gsc, connectGsc } = useScanFlow();
    const navigate = useNavigate();
    const draft = ctl.draft;
    const first = isFirstCheck(draft);
    const [, user] = useUserProfileContext();
    // The person's name, not their login handle; the handle only when no name is on the profile.
    const signedInName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username || "";

    const draftId = draft?.id ?? null;
    // Kept only once the signed-in staffer is known: never under a key another login can read.
    const fieldsOwner = (user?.id ? String(user.id) : "") || user?.username?.trim() || null;
    const storageKey = fieldsOwner && draftId ? fieldsKey(fieldsOwner, draftId) : null;
    const [storedFields, setStoredFields] = useState<{ key: string | null; value: StartFields }>(() => ({
        key: storageKey,
        value: (storageKey && loadFields(storageKey)) || EMPTY_START_FIELDS,
    }));
    useEffect(() => {
        setStoredFields((current) =>
            current.key === storageKey ? current : { key: storageKey, value: (storageKey && loadFields(storageKey)) || EMPTY_START_FIELDS },
        );
    }, [storageKey]);
    const fields = storedFields.key === storageKey ? storedFields.value : EMPTY_START_FIELDS;
    useEffect(() => {
        if (storageKey && storedFields.key === storageKey) saveFields(storageKey, storedFields.value);
    }, [storageKey, storedFields]);
    const updateFields = (patch: Partial<StartFields>) =>
        setStoredFields((current) => ({
            key: storageKey,
            value: { ...(current.key === storageKey ? current.value : EMPTY_START_FIELDS), ...patch },
        }));
    // The server's refusal to start, shown in the step (a First AI check's guards).
    const [refusal, setRefusal] = useState<string | null>(null);

    const requestedBy = fields.requestedBy ?? (draft?.summary.requestedBy || draft?.business.requestedBy || signedInName);
    const note = fields.note ?? (first ? FIRST.why(draft?.firstCheck?.quote?.ref ?? null) : "");

    const [confirming, setConfirming] = useState(false);
    // A changed set on a business with a baseline: keep it (default) or move it, with a reason.
    const baselineFacts = !first && draft?.baseline ? draft.baseline : null;
    const baselineMovable = !!baselineFacts?.movableUntil && Date.now() <= new Date(baselineFacts.movableUntil).getTime();
    const [baselineMode, setBaselineMode] = useState<"keep" | "move">("keep");
    const [baselineReason, setBaselineReason] = useState("");
    const [reasonError, setReasonError] = useState(false);
    const moving = baselineMovable && baselineMode === "move";
    // Monthly checks run: the new set can wait for the next one, free (the default), or rescan now.
    const monthlyAt = !first && draft?.monthly?.nextAt ? draft.monthly.nextAt : null;
    const [applyChoice, setApplyChoice] = useState<"next_monthly" | "now">("next_monthly");
    const applyLater = !!monthlyAt && applyChoice === "next_monthly" && !moving;
    // "Use the pitch scan" answered on Review: Start takes over the running pitch, nothing is paid.
    const usesPitch = first && !!draft?.decisions.some((d) => d.type === "pitch_running" && d.state === "resolved");
    const free = applyLater || usesPitch;
    // Set from the click until the page has left, so the started draft never flashes its "started" view.
    const [launching, setLaunching] = useState(false);
    const busy = ctl.busy;
    const starting = busy === "Starting";
    /*
     * A First AI check whose run failed starts again from the same approved 50
     * (H4-a): its draft still reads "started", so the onboarding record says
     * whether that run failed. Checking, confirmed active, confirmed failed and
     * a lookup that failed are four states: a failed lookup is never read as a
     * run that is going fine, and the started view waits for the answer (Jack, #4288).
     */
    const startedFirst = first && draft?.status === "started" && draft.businessId ? draft.businessId : null;
    const [existingRun, setExistingRun] = useState<"idle" | "checking" | "active" | "failed" | "error">("idle");
    const [runCheckTry, setRunCheckTry] = useState(0);
    useEffect(() => {
        if (!startedFirst) {
            setExistingRun("idle");
            return;
        }
        let cancelled = false;
        setExistingRun("checking");
        getOnboarding(startedFirst).then(
            (o) => {
                if (cancelled) return;
                if (!o?.scan) setExistingRun("error");
                else setExistingRun(o.scan.status === "failed" ? "failed" : "active");
            },
            () => {
                if (!cancelled) setExistingRun("error");
            },
        );
        return () => {
            cancelled = true;
        };
    }, [startedFirst, runCheckTry]);
    const restartable = !!startedFirst && existingRun === "failed";
    const checkingExistingRun = !!startedFirst && (existingRun === "checking" || existingRun === "idle");
    const runCheckFailed = !!startedFirst && existingRun === "error";
    const approved = draft?.status === "approved" || restartable;
    // A brand set with rows for a market the capture cannot reach yet cannot start (SPEC-v45 §12.1).
    const notCaptured = draft ? marketsNotCaptured(draft) : [];
    const blocked = notCaptured.length > 0;
    // A string, so the foot effect below re-runs only when the wording changes.
    const waitingLine = blocked ? COPY.footWaiting(notCaptured) : null;
    const started = draft?.status === "started" && !launching && (!startedFirst || existingRun === "active");

    useEffect(() => {
        if (!draft) {
            foot.set(null);
            return;
        }
        if (started) {
            // An empty notice leaves quietly: no "Draft saved" line after a scan has started.
            // The business it scans, when Oura has it: its page is where the scan's progress and results show.
            const bid = draft.businessId;
            const name = draft.business.businessName.trim() || draft.domain;
            foot.set(
                bid && !first
                    ? {
                          secondary: { label: COPY.startedClose, onClick: () => close({ notice: "" }) },
                          primary: { label: COPY.openBusiness(name), onClick: () => navigate(`${OURA_ROOT}/businesses/${encodeURIComponent(bid)}`) },
                      }
                    : { primary: { label: first || kind !== "new_scan" ? COPY.startedClose : COPY.toList, onClick: () => close({ notice: "" }) } },
            );
            return;
        }

        const back = async () => {
            setConfirming(false);
            const failed = await ctl.reopen();
            if (!failed) goTo("review");
        };

        const start = async () => {
            if (!draftId) return;
            setLaunching(true);
            setRefusal(null);
            const res = await ctl.start({
                note: note.trim() || null,
                requestedBy: requestedBy.trim() || null,
                ...(baselineFacts ? { baseline: moving ? "move" : "keep", baselineReason: moving ? baselineReason.trim() : null } : {}),
                ...(monthlyAt ? { applyAt: applyLater ? "next_monthly" : "now" } : {}),
            });
            if (!res.ok) {
                // The foot's message line says why (the controller sets it). A First AI
                // check's guard refusals stand in the step instead, with the server's sentence.
                if (res.code && START_REFUSALS.has(res.code)) {
                    setRefusal(res.message);
                    ctl.clearMessage();
                }
                setLaunching(false);
                setConfirming(false);
                return;
            }
            if (storageKey) saveFields(storageKey, null);
            close({
                notice: scanDraftStarted(res.data, { kind, purpose, domain: draft.domain, businessName: draft.business.businessName }).scanDraftNotice,
            });
        };

        foot.set({
            // The cost is in the paid note above and again in the confirm row: the foot does not say it a third time.
            status: waitingLine ?? undefined,
            back: { onClick: () => void back(), busy: busy === "Reopening", disabled: Boolean(busy) || launching },
            secondary: { label: COPY.save, onClick: () => close(), disabled: Boolean(busy) || launching },
            primary: {
                label: first ? FIRST.title : applyLater ? WHEN.startLater : COPY.start,
                icon: PlayIcon,
                onClick: () => {
                    // Moving the baseline needs its reason before anything is spent.
                    if (moving && !baselineReason.trim()) {
                        setReasonError(true);
                        requestAnimationFrame(() => document.getElementById("newscan-baseline-reason")?.focus());
                        return;
                    }
                    // Nothing is spent (the next monthly check, or the pitch's own run): no paid confirm.
                    if (free) {
                        void start();
                        return;
                    }
                    setConfirming(true);
                },
                disabled: !approved || blocked || Boolean(busy) || launching,
            },
            confirm: confirming && !blocked
                ? {
                      text: COPY.confirm(draft.summary.estimatedCostUsd),
                      verb: COPY.confirmVerb,
                      busy: starting || launching,
                      onConfirm: () => void start(),
                      onCancel: () => setConfirming(false),
                  }
                : null,
        });
    }, [
        draft,
        draftId,
        storageKey,
        started,
        approved,
        blocked,
        waitingLine,
        busy,
        starting,
        launching,
        confirming,
        note,
        requestedBy,
        baselineFacts,
        moving,
        baselineReason,
        monthlyAt,
        applyLater,
        free,
        kind,
        purpose,
        first,
        ctl,
        foot,
        goTo,
        close,
        navigate,
    ]);

    if (!draft) return null;

    if (started) {
        if (draft.scheduledFor) return <StepHead title={WHEN.startedTitle} sentence={WHEN.startedLine(shortDate(draft.scheduledFor) ?? "")} />;
        return first ? (
            <StepHead title={FIRST.startedTitle} sentence={FIRST.startedLine} />
        ) : (
            <StepHead title={COPY.startedTitle} sentence={COPY.startedLine} />
        );
    }

    const total = draft.counts.total || draft.questions.length;
    const changes = changesText(draft);
    const changedHere = draft.diff?.changed.length ?? draft.changedVsPitch ?? 0;
    const brand = isBrandSet(draft);
    const partners = (draft.business.partnerProducts ?? []).map((p) => p.trim()).filter(Boolean);
    const businessName = draft.business.businessName.trim() || draft.domain;
    const carried = first ? carriedText(draft) : null;
    const forPlan = first ? forPlanText(draft) : null;
    // A First AI check comes before the access request: Search Console shows only if already connected.
    const showGsc = !first || draft.search.connected || gsc.status === "connected";

    return (
        <div>
            {first ? (
                <StepHead title={FIRST.title} sentence={FIRST.sentence(businessName)} />
            ) : (
                <StepHead title={applyLater ? WHEN.title : COPY.title} sentence={applyLater ? WHEN.frozen(total) : COPY.frozen(total, kind === "new_scan")} />
            )}

            <Surface>
                <dl className="m-0">
                    <DefRow label={COPY.questions}>
                        <Fact>
                            <span className="tabular-nums">
                                {total} · {brand ? brandShape(draft) : shapeFromDraft(draft)}
                            </span>
                        </Fact>
                    </DefRow>
                    {brand && (
                        <>
                            <DefRow label={COPY.markets}>
                                <Fact>
                                    <span className="tabular-nums">{marketLine(draft)}</span>
                                </Fact>
                            </DefRow>
                            {draft.demand && (
                                <DefRow label={COPY.searches}>
                                    <Fact>
                                        <span className="tabular-nums">{COPY.pulled(shortDate(draft.demand.pulledAt), draft.demand.suggestions)}</span>
                                    </Fact>
                                </DefRow>
                            )}
                            <DefRow label={COPY.partners}>
                                <Fact>{partners.length ? partners.join(", ") : <span className="text-gray-500">{COPY.none}</span>}</Fact>
                            </DefRow>
                            <DefRow label={COPY.win}>
                                <p className={cn(TYPE.tc, "m-0 break-words py-1.5 text-gray-600")}>{COPY.winLine(partners.length)}</p>
                            </DefRow>
                        </>
                    )}
                    {first && (
                        <>
                            <DefRow label={FIRST.from}>
                                <Fact>
                                    <span className="tabular-nums">{fromText(draft)}</span>
                                    {carried && <span className="block text-gray-500">{carried}</span>}
                                </Fact>
                            </DefRow>
                            <DefRow label={FIRST.forPlan}>
                                <Fact>{forPlan ? <span className="tabular-nums">{forPlan}</span> : <span className="text-gray-500">{FIRST.nothingForPlan}</span>}</Fact>
                            </DefRow>
                        </>
                    )}
                    <DefRow label={COPY.recipe}>
                        <Fact>{recipeText(draft)}</Fact>
                    </DefRow>
                    {first ? (
                        // The pitch's own edits travel with its 50 ("From" says how many were kept); only this draft's count here.
                        <DefRow label={FIRST.changedHere}>
                            <Fact>
                                {changedHere > 0 ? (
                                    <span className="tabular-nums">{FIRST.changedCount(changedHere, total)}</span>
                                ) : (
                                    <span className="text-gray-500">{COPY.noChanges}</span>
                                )}
                            </Fact>
                        </DefRow>
                    ) : (
                        <DefRow label={COPY.changes}>
                            <Fact>{changes ?? <span className="text-gray-500">{COPY.noChanges}</span>}</Fact>
                        </DefRow>
                    )}
                    {showGsc && (
                        <DefRow label={COPY.gsc}>
                            <SearchConsoleFact draft={draft} gscStatus={gsc.status} gscError={gsc.error} onConnect={() => void connectGsc()} />
                        </DefRow>
                    )}
                    {baselineFacts && (
                        <DefRow label={BASELINE.label}>
                            <BaselineChoice
                                day={shortDate(baselineFacts.at) ?? ""}
                                movable={baselineMovable}
                                mode={baselineMode}
                                onMode={(m) => {
                                    setBaselineMode(m);
                                    setReasonError(false);
                                }}
                                reason={baselineReason}
                                onReason={(v) => {
                                    setBaselineReason(v);
                                    if (v.trim()) setReasonError(false);
                                }}
                                reasonError={reasonError}
                                trend={draft.trendBreak?.willBreak ? draft.trendBreak.reason : null}
                                disabled={launching}
                            />
                        </DefRow>
                    )}
                    {monthlyAt && (
                        <DefRow label={WHEN.label}>
                            <div className="space-y-2 py-1.5">
                                <RadioSeg<"next_monthly" | "now">
                                    options={[
                                        { key: "next_monthly", label: WHEN.later },
                                        { key: "now", label: WHEN.now },
                                    ]}
                                    value={moving ? "now" : applyChoice}
                                    onChange={setApplyChoice}
                                    ariaLabel={WHEN.label}
                                    wrapOnPhone
                                    disabled={launching || moving}
                                />
                                <p className={cn(TYPE.tc, "m-0 break-words text-gray-600")}>
                                    {moving
                                        ? WHEN.moveNeedsNow
                                        : applyLater
                                          ? WHEN.laterLine(shortDate(monthlyAt) ?? "")
                                          : WHEN.nowLine(draft.summary.estimatedCostUsd)}
                                </p>
                            </div>
                        </DefRow>
                    )}
                    <StartFieldsRows
                        requestedBy={requestedBy}
                        note={note}
                        example={COPY.whyExample[kind]}
                        disabled={launching}
                        onRequestedBy={(v) => updateFields({ requestedBy: v })}
                        onNote={(v) => updateFields({ note: v })}
                    />
                </dl>
            </Surface>

            {refusal && <Refusal className="mt-4">{refusal}</Refusal>}
            {checkingExistingRun && <p className={cn(TYPE.tc, "m-0 mt-4 text-gray-600")}>{COPY.checkingRun}</p>}
            {runCheckFailed && (
                <div className="mt-4 space-y-2">
                    <Refusal>{COPY.runCheckFailed}</Refusal>
                    <PillButton onClick={() => setRunCheckTry((n) => n + 1)}>{COPY.retry}</PillButton>
                </div>
            )}

            {blocked ? (
                <p className={cn(TYPE.tc, "m-0 mt-4 flex items-start gap-2.5 rounded-xl bg-gray-100 px-4 py-3 text-gray-600")}>
                    <GlobeIcon aria-hidden size={15} className="mt-px shrink-0 text-gray-500" />
                    <span className="min-w-0">{COPY.notYet(notCaptured)}</span>
                </p>
            ) : free ? (
                // Nothing is spent: a grey note, never the violet paid one.
                <p className={cn(TYPE.tc, "m-0 mt-4 flex items-start gap-2.5 rounded-xl bg-gray-100 px-4 py-3 text-gray-600")}>
                    <CheckIcon aria-hidden size={15} className="mt-px shrink-0 text-gray-500" />
                    <span className="min-w-0">
                        <span className="font-medium text-gray-900">{usesPitch ? PITCH_RUN.lead : WHEN.freeLead}</span>{" "}
                        {usesPitch ? PITCH_RUN.line(pitchMinutesLeft(draft)) : WHEN.free(shortDate(monthlyAt) ?? "")}
                    </span>
                </p>
            ) : (
                <p className={cn(TYPE.tc, "m-0 mt-4 flex items-start gap-2.5 rounded-xl bg-violet-50 px-4 py-3 text-gray-600")}>
                    <PlayIcon aria-hidden size={15} className="mt-px shrink-0 text-violet-600" />
                    <span className="min-w-0">
                        <span className="font-medium text-gray-900">{first ? FIRST.paidLead : COPY.paidLead}</span>{" "}
                        {first
                            ? FIRST.paid(total, FIRST_CHECK_MINUTES, draft.summary.estimatedCostUsd)
                            : COPY.paid(total, FIRST_CHECK_MINUTES, draft.summary.estimatedCostUsd)}
                    </span>
                </p>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Pieces
 * ------------------------------------------------------------------------- */

/** Minutes left on the pitch scan this first check takes over: from its start, one duration everywhere. */
function pitchMinutesLeft(draft: ScanDraft): number {
    const at = draft.firstCheck?.pitch?.scannedAt;
    const left = at ? FIRST_CHECK_MINUTES - Math.floor((Date.now() - new Date(at).getTime()) / 60_000) : FIRST_CHECK_MINUTES;
    return Math.min(FIRST_CHECK_MINUTES, Math.max(1, left));
}

/** A fact in a row: body text, set on the label's line. */
function Fact({ children }: { children: ReactNode }) {
    return <p className={cn(TYPE.tb, "m-0 break-words py-1.5 text-gray-900")}>{children}</p>;
}

function SearchConsoleFact({
    draft,
    gscStatus,
    gscError,
    onConnect,
}: {
    draft: ScanDraft;
    gscStatus: "connected" | "not_connected" | "action_needed" | "connecting";
    gscError: string | null;
    onConnect: () => void;
}) {
    const search = draft.search;
    const connected = search.connected || gscStatus === "connected";
    const focus = useConnectedFocus<HTMLParagraphElement>(connected);
    const connecting = gscStatus === "connecting";
    const detail = search.pulling ? COPY.gscPulling : search.matched > 0 ? COPY.gscMatched(search.matched) : search.absentReason;
    return (
        <>
            {connected ? (
                <p ref={focus.targetRef} tabIndex={-1} className={cn(TYPE.tb, "m-0 flex items-start gap-x-1.5 py-1.5 text-gray-900 outline-none")}>
                    <CheckIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span className="min-w-0 break-words">
                        {COPY.gscConnected}
                        {detail ? <span className="tabular-nums">{` · ${detail}`}</span> : null}
                    </span>
                </p>
            ) : (
                <p className={cn(TYPE.tb, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 py-1.5")}>
                    <span className="text-gray-500">{COPY.gscNot}</span>
                    <PillButton
                        variant="text"
                        busy={connecting}
                        onClick={() => {
                            focus.asked();
                            onConnect();
                        }}
                    >
                        {connecting ? COPY.gscConnecting : COPY.gscConnect}
                    </PillButton>
                    <Muted tone="ink4" className="max-sm:basis-full">
                        <span aria-hidden className="max-sm:hidden">
                            ·{" "}
                        </span>
                        {COPY.gscWhy}
                    </Muted>
                </p>
            )}
            {gscError && !connected && (
                <p role="alert" className={cn(TYPE.tc, "m-0 font-medium text-rose-600")}>
                    {gscError}
                </p>
            )}
            <span role="status" className="sr-only">
                {focus.said}
            </span>
        </>
    );
}

/** The baseline row: keep it (a new set) or, in its first 30 days, move it with a reason. */
function BaselineChoice({
    day,
    movable,
    mode,
    onMode,
    reason,
    onReason,
    reasonError,
    trend,
    disabled,
}: {
    day: string;
    movable: boolean;
    mode: "keep" | "move";
    onMode: (m: "keep" | "move") => void;
    reason: string;
    onReason: (v: string) => void;
    reasonError: boolean;
    /** The trend warning the Review acknowledged, said again where the money is spent. */
    trend: string | null;
    disabled: boolean;
}) {
    const id = useId();
    const moving = movable && mode === "move";
    return (
        <div className="space-y-2 py-1.5">
            {movable ? (
                <RadioSeg<"keep" | "move">
                    options={[
                        { key: "keep", label: BASELINE.keep },
                        { key: "move", label: BASELINE.move },
                    ]}
                    value={mode}
                    onChange={onMode}
                    ariaLabel={BASELINE.label}
                    wrapOnPhone
                    disabled={disabled}
                />
            ) : (
                <p className={cn(TYPE.tb, "m-0 text-gray-900")}>{BASELINE.locked(day, BASELINE_DAYS)}</p>
            )}
            <p className={cn(TYPE.tc, "m-0 break-words text-gray-600")}>
                {[trend, moving ? BASELINE.moveLine(day) : BASELINE.keepLine(day)].filter(Boolean).join(" ")}
            </p>
            {moving && (
                <div>
                    <label htmlFor="newscan-baseline-reason" className={cn(TYPE.tc, "font-medium text-gray-700")}>
                        {BASELINE.reason}
                    </label>
                    <textarea
                        id="newscan-baseline-reason"
                        rows={2}
                        aria-invalid={reasonError || undefined}
                        aria-describedby={`${id}-hint`}
                        className={cn(textareaClass(reasonError ? BASELINE.reasonNeeded : undefined), "mt-1 block resize-y")}
                        value={reason}
                        placeholder={BASELINE.reasonPlaceholder}
                        disabled={disabled}
                        onChange={(e) => onReason(e.target.value)}
                    />
                    <p id={`${id}-hint`} className={cn(TYPE.tc, "m-0 mt-1", reasonError ? "font-medium text-rose-600" : "text-gray-500")}>
                        {reasonError ? BASELINE.reasonNeeded : BASELINE.reasonHint}
                    </p>
                </div>
            )}
        </div>
    );
}

function StartFieldsRows({
    requestedBy,
    note,
    example,
    disabled,
    onRequestedBy,
    onNote,
}: {
    requestedBy: string;
    note: string;
    example: string;
    disabled: boolean;
    onRequestedBy: (v: string) => void;
    onNote: (v: string) => void;
}) {
    const id = useId();
    const whoId = `${id}-who`;
    const whyId = `${id}-why`;
    const whyHintId = `${id}-why-hint`;
    return (
        <>
            <DefRow label={COPY.requestedBy} labelFor={whoId}>
                <input
                    id={whoId}
                    className={cn(inputClass(), "sm:max-w-sm")}
                    value={requestedBy}
                    placeholder={COPY.requestedByPlaceholder}
                    autoComplete="off"
                    disabled={disabled}
                    onChange={(e) => onRequestedBy(e.target.value)}
                />
            </DefRow>
            <DefRow label={COPY.why} labelFor={whyId}>
                <textarea
                    id={whyId}
                    rows={2}
                    aria-describedby={whyHintId}
                    className={cn(textareaClass(), "block resize-y")}
                    value={note}
                    placeholder={example}
                    disabled={disabled}
                    onChange={(e) => onNote(e.target.value)}
                />
                <Muted as="p" id={whyHintId}>
                    {COPY.whyHint}
                </Muted>
            </DefRow>
        </>
    );
}
